import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useStore } from "@/context/StoreContext";
import { currentStore } from "@/mocks/dashboard";

interface TopHeaderProps {
  title: string;
  onMenuClick?: () => void;
}

export default function TopHeader({ title, onMenuClick }: TopHeaderProps) {
  const navigate = useNavigate();
  const { notifications } = useStore();
  const [openPanel, setOpenPanel] = useState<"none" | "bell" | "user">("none");
  const wrapperRef = useRef<HTMLDivElement>(null);

  const unread = notifications.filter((item) => !item.isRead).length;

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setOpenPanel("none");
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <header className="h-16 shrink-0 bg-white border-b border-background-200 flex items-center justify-between gap-3 px-4 md:px-6 sticky top-0 z-40">
      <div className="flex items-center gap-3 min-w-0">
        {onMenuClick && (
          <button
            type="button"
            onClick={onMenuClick}
            className="lg:hidden w-10 h-10 flex items-center justify-center rounded-full text-foreground-700 hover:bg-background-100 transition-colors duration-200 cursor-pointer shrink-0"
            aria-label="فتح القائمة"
          >
            <i className="ri-menu-line text-xl"></i>
          </button>
        )}
        <h1 className="text-lg md:text-xl font-bold text-foreground-950 truncate">
          {title}
        </h1>
      </div>

      <div ref={wrapperRef} className="flex items-center gap-2 sm:gap-3 shrink-0">
        <div className="relative">
          <button
            type="button"
            onClick={() =>
              setOpenPanel((prev) => (prev === "bell" ? "none" : "bell"))
            }
            className="relative w-10 h-10 flex items-center justify-center rounded-full text-foreground-700 hover:bg-background-100 transition-colors duration-200 cursor-pointer"
            aria-label="الإشعارات"
          >
            <i className="ri-notification-3-line text-xl"></i>
            {unread > 0 && (
              <span className="absolute top-2 right-2 w-2.5 h-2.5 rounded-full bg-red-500 ring-2 ring-white"></span>
            )}
          </button>

          {openPanel === "bell" && (
            <div className="yc-pop absolute left-0 mt-2 w-72 sm:w-80 rounded-card bg-white shadow-pop border border-background-200 overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-background-200">
                <span className="text-sm font-bold text-foreground-950">
                  الإشعارات
                </span>
                <span className="text-xs font-medium text-primary-600">
                  {unread} غير مقروء
                </span>
              </div>
              <div className="max-h-72 overflow-y-auto">
                {notifications.slice(0, 4).map((item) => (
                  <div
                    key={item.id}
                    className="flex gap-3 px-4 py-3 border-b border-background-100 last:border-0 hover:bg-background-50 transition-colors"
                  >
                    <span className="w-8 h-8 shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                      <i className={item.icon}></i>
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-foreground-900 truncate">
                        {item.title}
                      </p>
                      <p className="text-xs text-foreground-500 line-clamp-2 mt-0.5">
                        {item.body}
                      </p>
                      <p className="text-[11px] text-foreground-400 mt-1">
                        {item.time}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={() => {
                  setOpenPanel("none");
                  navigate("/notifications");
                }}
                className="w-full py-2.5 text-sm font-medium text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer"
              >
                عرض كل الإشعارات
              </button>
            </div>
          )}
        </div>

        <div className="relative">
          <button
            type="button"
            onClick={() =>
              setOpenPanel((prev) => (prev === "user" ? "none" : "user"))
            }
            className="flex items-center gap-2 rounded-full pl-1 pr-2 py-1 hover:bg-background-100 transition-colors duration-200 cursor-pointer"
          >
            <img
              src={currentStore.logo}
              alt={currentStore.ownerName}
              className="w-8 h-8 rounded-full object-cover"
            />
            <span className="text-sm font-medium text-foreground-800 hidden sm:block">
              {currentStore.ownerName}
            </span>
            <span className="w-5 h-5 flex items-center justify-center text-foreground-500">
              <i className="ri-arrow-down-s-line"></i>
            </span>
          </button>

          {openPanel === "user" && (
            <div className="yc-pop absolute left-0 mt-2 w-56 rounded-card bg-white shadow-pop border border-background-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-background-200">
                <p className="text-sm font-bold text-foreground-950">
                  {currentStore.ownerName}
                </p>
                <p className="text-xs text-foreground-500 mt-0.5">
                  {currentStore.brandName}
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setOpenPanel("none");
                  navigate("/settings");
                }}
                className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-50 transition-colors cursor-pointer"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-user-settings-line"></i>
                </span>
                إعدادات الحساب
              </button>
              <button
                type="button"
                onClick={() => {
                  setOpenPanel("none");
                  navigate("/login");
                }}
                className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-logout-box-r-line"></i>
                </span>
                تسجيل الخروج
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}